import NotificationService from './dialogs/notifications';
import { MediaUserRole } from './liveMedia.service';
import loggerService from './logger.service';

import type { ICameraVideoTrack } from '@/services/agora.types';

import { AGORA } from '@/constants';
import store from '@/store';

export enum SPEAKERS_REMOTE_STREAM_COUNT {
  LOW = 8,
  MEDIUM = 12,
  LARGE = 16
}

export enum SPEAKERS_1080P_THRESHOLDS {
  SIX_SPEAKERS = 6, // 1080p
  TEN_SPEAKERS = 10, // 720p
  SIXTEEN_SPEAKERS = 16 // 480p (360p while Use_Skip_480 is on)
}

export interface VideoEncoderConfiguration {
  name: string;
  value: string;
}

// Full ordered ladder. Auto mode moves one rung up or down per tick.
export const VideoQualities: VideoEncoderConfiguration[] = [
  { name: 'POOR', value: '180p_1' }, // 15fps
  { name: 'LOWEST', value: '180p_1' }, // 15fps
  { name: 'LOW', value: '240p_4' }, // 15fps
  { name: 'MEDIUM', value: '360p_9' }, // 15fps
  { name: 'REGULAR', value: '480p_8' }, // 15fps
  { name: 'HIGH', value: '720p_1' } // 15fps
];

export const DEFAULT_VIDEO_QUALITY_NAME = 'REGULAR';
export const BACKSTAGE_VIDEO_QUALITY_NAME = 'REGULAR';
export const MAX_VIDEO_QUALITY_NAME_WITH_VB = 'REGULAR';
const ULTRA_HIGH_VIDEO_QUALITY: VideoEncoderConfiguration = { name: 'ULTRA_HIGH', value: '1080p_1' }; // 15fps

// The 480p_8 preset produces corrupted video on Agora. While Use_Skip_480 is on, the REGULAR rung
// is dropped from the active ladder and anything still referencing it resolves to MEDIUM.
const SKIPPED_VIDEO_QUALITY_NAME = 'REGULAR';
const SKIPPED_VIDEO_QUALITY_REPLACEMENT_NAME = 'MEDIUM';

export function isSkip480Enabled(): boolean {
  return !!store.getters['event/useSkip480'];
}

export function getVideoQualities(): VideoEncoderConfiguration[] {
  return isSkip480Enabled() ? VideoQualities.filter(vq => vq.name !== SKIPPED_VIDEO_QUALITY_NAME) : VideoQualities;
}

export function getSupportedVideoQualityName(name: string): string {
  return isSkip480Enabled() && name === SKIPPED_VIDEO_QUALITY_NAME ? SKIPPED_VIDEO_QUALITY_REPLACEMENT_NAME : name;
}

export function getSupportedEncoderPreset(preset: string): string {
  const skipped = VideoQualities.find(vq => vq.name === SKIPPED_VIDEO_QUALITY_NAME);
  return isSkip480Enabled() && preset === skipped?.value
    ? getVideoQuality(SKIPPED_VIDEO_QUALITY_REPLACEMENT_NAME).value
    : preset;
}

export function getVideoQualityIndex(name: string): number {
  return getVideoQualities().findIndex(vq => vq.name === getSupportedVideoQualityName(name));
}

export function getVideoQuality(name: string): VideoEncoderConfiguration {
  const quality = getVideoQualities()[getVideoQualityIndex(name)];
  if (!quality) {
    throw new Error(`[VIDEO QUALITY] Unknown video quality: ${name}`);
  }
  return quality;
}

// 1080p is only part of the ladder when the org has Full HD enabled.
function ensureUltraHighRung(): VideoEncoderConfiguration {
  if (!VideoQualities.some(vq => vq.name === ULTRA_HIGH_VIDEO_QUALITY.name)) {
    VideoQualities.push(ULTRA_HIGH_VIDEO_QUALITY);
  }
  return ULTRA_HIGH_VIDEO_QUALITY;
}

function getDefaultVideoQualityConstant(): VideoEncoderConfiguration {
  return store.getters['event/useFullHD'] ? ensureUltraHighRung() : getVideoQuality(DEFAULT_VIDEO_QUALITY_NAME);
}

export const updateDefaultVideoQualityConstant = () => {
  getDefaultVideoQualityConstant();
};

type VideoQualityHandlerMeta = {
  quality: VideoEncoderConfiguration;
  interval: NodeJS.Timeout;
  warned: boolean;
};

export const videoMetaMap = new Map<string, VideoQualityHandlerMeta>();

// Calculate ideal quality based on speaker count
function calculateIdealQuality(channelName: string): VideoEncoderConfiguration | undefined {
  const { remoteStreams, userRole } = store.state.mediaroom?.mediaRooms?.[channelName] ?? {};
  if (!remoteStreams) {
    return;
  }

  const remoteStreamCount = Object.keys(remoteStreams).length;
  const isCurrentUserParticipating = userRole === MediaUserRole.host || store.state.mediaroom.localTracks?.length > 0;
  const totalParticipants = remoteStreamCount + (isCurrentUserParticipating ? 1 : 0);
  const streamCount = totalParticipants;

  if (store.getters['event/useFullHD']) {
    loggerService.log('[VIDEO QUALITY] Calculating ideal quality', {
      streamCount
    });

    if (streamCount <= SPEAKERS_1080P_THRESHOLDS.SIX_SPEAKERS) {
      return ensureUltraHighRung();
    }
    if (streamCount <= SPEAKERS_1080P_THRESHOLDS.TEN_SPEAKERS) {
      return getVideoQuality('HIGH');
    }
    if (streamCount <= SPEAKERS_1080P_THRESHOLDS.SIXTEEN_SPEAKERS) {
      return getVideoQuality('REGULAR');
    }
    return getVideoQuality('LOW');
  }

  // Legacy logic
  if (streamCount < SPEAKERS_REMOTE_STREAM_COUNT.LOW) {
    return;
  }
  if (streamCount < SPEAKERS_REMOTE_STREAM_COUNT.MEDIUM) {
    return getVideoQuality('LOW');
  }
  if (streamCount < SPEAKERS_REMOTE_STREAM_COUNT.LARGE) {
    return getVideoQuality('LOWEST');
  }
  return getVideoQuality('POOR');
}

// Apply restrictions on top of ideal quality
function applyQualityRestrictions(
  idealQuality: VideoEncoderConfiguration | undefined,
  channelName: string
): VideoEncoderConfiguration | undefined {
  if (!idealQuality) {
    return idealQuality;
  }
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return getVideoQuality(BACKSTAGE_VIDEO_QUALITY_NAME);
  }

  const selectedVideoQuality = store.state.user.selected_video_quality;
  if ((selectedVideoQuality as any)?.name !== 'AUTO') {
    loggerService.log(
      '[VIDEO QUALITY] Using configurable video quality, so not handling video quality based on calculations'
    );
    return;
  }

  return idealQuality;
}

function processVideoQualityForTrack(track: ICameraVideoTrack, channelName: string) {
  const trackId = track.getTrackId();
  const mediaroom = store.state.mediaroom.mediaRooms?.[channelName];
  if (!mediaroom?.client) {
    return;
  }
  const localVideoStats = mediaroom.client.getLocalVideoStats();

  const { sendFrameRate, sendPacketsLost } = localVideoStats;

  if (!sendFrameRate || sendFrameRate <= 0) {
    return;
  }

  const meta = videoMetaMap.get(trackId);

  loggerService.log(
    '[VIDEO QUALITY] Current video quality status',
    JSON.stringify({
      sendFrameRate,
      sendPacketsLost,
      currentQuality: meta?.quality
    })
  );

  if (!meta) {
    loggerService.warn('[VIDEO QUALITY] No meta set to track video');
    return;
  }

  const currentQualityIndex = getVideoQualities().findIndex(
    (vq: VideoEncoderConfiguration) => vq.value === meta.quality.value
  );

  if (
    channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE) &&
    currentQualityIndex >= getVideoQualityIndex(BACKSTAGE_VIDEO_QUALITY_NAME)
  ) {
    return;
  }

  if (meta.quality.name === ULTRA_HIGH_VIDEO_QUALITY.name && !store.getters['event/useFullHD']) {
    loggerService.warn('[VIDEO QUALITY] 1080p is disabled - downgrading to 720p');
    downgradeVideoQuality(track, getVideoQualityIndex('HIGH') + 1);
    return;
  }

  const selectedVideoQuality = store.state.user.selected_video_quality;

  if ((selectedVideoQuality as any)?.name !== 'AUTO') {
    loggerService.log(
      '[VIDEO QUALITY] Using configurable video quality, so not handling video quality based on calculations'
    );
    return;
  }

  const fixMaxVideoQualityWithVb = store.state.event?.organization?.fix_max_video_quality_with_vb;
  const maxQualityIndexWithVb = getVideoQualityIndex(MAX_VIDEO_QUALITY_NAME_WITH_VB);

  if (
    store.state.mediaroom.currentBackgroundFilter &&
    currentQualityIndex > maxQualityIndexWithVb &&
    fixMaxVideoQualityWithVb &&
    !store.getters['event/useFullHD']
  ) {
    loggerService.warn(
      "[VIDEO QUALITY] VB video quality downgrade - won't publish above the VB cap because VB is applied",
      currentQualityIndex
    );
    downgradeVideoQuality(track, maxQualityIndexWithVb + 1);
    return;
  }

  if (sendFrameRate >= AGORA.GOOD_FPS) {
    upgradeVideoQuality(track, currentQualityIndex);
    return;
  }
  if (sendFrameRate < AGORA.FPS_FALL_THRESHOLD) {
    const isLocalRecording = store.state.event.event.is_local_recording;
    // During local recording, fps below FPS_LOCAL_RECORDING_AGGRESSIVE_THRESHOLD indicates severe
    // CPU/IO pressure from the recorder itself, so skip straight to minimum quality.
    // fps in [FPS_LOCAL_RECORDING_AGGRESSIVE_THRESHOLD, FPS_FALL_THRESHOLD) is degraded but not
    // critical enough to justify a full jump — keep the standard single-step downgrade there.
    const steps =
      isLocalRecording && sendFrameRate < AGORA.FPS_LOCAL_RECORDING_AGGRESSIVE_THRESHOLD ? currentQualityIndex - 1 : 1;
    downgradeVideoQuality(track, currentQualityIndex, steps);
  }
}

export function handleVideoQualities(
  track: ICameraVideoTrack,
  channelName: string,
  videoQualityValue: VideoEncoderConfiguration['value']
) {
  const trackId = track.getTrackId();
  const interval = setInterval(() => {
    processVideoQualityForTrack(track, channelName);
  }, 30000);

  const supportedPreset = getSupportedEncoderPreset(videoQualityValue);
  videoMetaMap.set(trackId, {
    quality: getVideoQualities().find(vq => vq.value === supportedPreset) || getDefaultVideoQualityConstant(),
    interval,
    warned: false
  });
}

export function getDefaultVideoQuality(channelName: string) {
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return getVideoQuality(BACKSTAGE_VIDEO_QUALITY_NAME).value;
  }

  const isBroadcast = store.state.route.path.includes('/broadcast');
  const { room_video_quality } = store.state.event.event;

  let selectedVideoQuality = getDefaultVideoQualityConstant();
  const selectedVideoQualityName =
    store.state.user?.selected_video_quality?.name || store.state.event.event?.video_quality;
  if (selectedVideoQualityName) {
    const supportedName = getSupportedVideoQualityName(selectedVideoQualityName);
    selectedVideoQuality =
      getVideoQualities().find(vq => vq.name === supportedName) || getDefaultVideoQualityConstant();
  }

  // The backend stores room_video_quality as a preset string, not a ladder entry. Without this
  // lookup the value read as undefined and Agora fell back to its own 480p default on tech check.
  const roomVideoQuality =
    typeof room_video_quality === 'string'
      ? getVideoQualities().find(vq => vq.value === getSupportedEncoderPreset(room_video_quality))
      : room_video_quality;

  let defaultQuality = (isBroadcast ? selectedVideoQuality : roomVideoQuality) || getDefaultVideoQualityConstant();
  defaultQuality = getVideoQualityBasedOnRemoteStreamCount(channelName) || defaultQuality;
  return getSupportedEncoderPreset(defaultQuality.value);
}

export function getVideoQualityBasedOnRemoteStreamCount(channelName: string): undefined | VideoEncoderConfiguration {
  const idealQuality = calculateIdealQuality(channelName);
  return applyQualityRestrictions(idealQuality, channelName);
}

export function setVideoQualityToTrack(track: ICameraVideoTrack, quality: VideoEncoderConfiguration) {
  const meta = videoMetaMap.get(track.getTrackId());
  if (!meta) {
    return;
  }
  // The AUTO dropdown entry carries the 480p preset too; keep its name so AUTO detection still works.
  const supportedQuality = { ...quality, value: getSupportedEncoderPreset(quality.value) };
  meta.quality = supportedQuality;
  track.setEncoderConfiguration(supportedQuality.value);
}

function upgradeVideoQuality(track: ICameraVideoTrack, currentQualityIndex: number) {
  const meta = videoMetaMap.get(track.getTrackId());
  const ladder = getVideoQualities();
  const nextQualityIndex = currentQualityIndex + 1;
  const fixMaxVideoQualityWithVb = store.state.event?.organization?.fix_max_video_quality_with_vb;
  const useFullHD = store.getters['event/useFullHD'];

  if (!meta) {
    return;
  }

  if (nextQualityIndex > ladder.length - 1) {
    return;
  }

  const nextQuality = ladder[nextQualityIndex];
  if (nextQuality?.name === ULTRA_HIGH_VIDEO_QUALITY.name && !useFullHD) {
    loggerService.warn('[VIDEO QUALITY] 1080p not enabled - not upgrading to ULTRA_HIGH', currentQualityIndex);
    return;
  }

  if (
    store.state.mediaroom.currentBackgroundFilter &&
    nextQualityIndex > getVideoQualityIndex(MAX_VIDEO_QUALITY_NAME_WITH_VB) &&
    fixMaxVideoQualityWithVb &&
    !useFullHD
  ) {
    loggerService.warn('[VIDEO QUALITY] Video quality is already at max with VB - not upgrading', currentQualityIndex);
    return;
  }

  loggerService.info('[VIDEO QUALITY] Upgrading video quality', { nextQuality: nextQuality.value });

  meta.quality = nextQuality;
  track.setEncoderConfiguration(nextQuality.value);
}

function downgradeVideoQuality(track: ICameraVideoTrack, currentQualityIndex: number, steps = 1) {
  const meta = videoMetaMap.get(track.getTrackId());

  if (!meta) {
    return;
  }

  const lowestQualityIndex = getVideoQualityIndex('LOWEST');

  if (currentQualityIndex === lowestQualityIndex) {
    if (!meta.warned && AGORA.SHOW_AV_NOTIFICATIONS) {
      NotificationService.toast(
        '@@translate@@ Warning',
        "@@translate@@ We've detected a problem with your video. We recommend trying to close other browser tabs or other applications running on your device and checking/upgrading your internet connection to resolve this issue.",
        'warning',
        '',
        AGORA.AV_NOTIFICATION_TIMEOUT
      );
      meta.warned = true;
    }
    return;
  }

  if (currentQualityIndex < lowestQualityIndex) {
    return;
  }

  // POOR is reserved for the speaker-count fallback; Auto mode never steps down past LOWEST.
  const nextQuality = getVideoQualities()[Math.max(lowestQualityIndex, currentQualityIndex - steps)];

  loggerService.info('[VIDEO QUALITY] Downgrading video quality', { nextQuality: nextQuality.value, steps });

  meta.quality = nextQuality;
  track.setEncoderConfiguration(nextQuality.value);
}

export function getCurrentAutoQuality(channelName: string): VideoEncoderConfiguration | undefined {
  if (channelName.includes(AGORA.UID_SUFFIX.BACKSTAGE)) {
    return getVideoQuality(BACKSTAGE_VIDEO_QUALITY_NAME);
  }

  const idealQuality = calculateIdealQuality(channelName);
  const finalQuality = applyQualityRestrictions(idealQuality, channelName);

  return finalQuality;
}
